import type { ItemFieldRow } from './item-field-reader';
import type { CreatureAttacksDependencies } from './creature-attacks';
import { readCreaturePayloadStatuses } from './creature-status-reader';
import { createFormulaReadContext } from './formula-read-context';
import { readItemUuidSlot } from './inventory-raw-readers';

const arrayLike = (value: unknown): value is Record<string, unknown> => value !== null && typeof value === 'object';
export type CreatureShapeshiftFormResult = { ok: true; form: ItemFieldRow | null } | { ok: false; reason: string };

/** ft_shapeshift_form_arr_for_speeds: the first held creature status supplies
 * the physical base for senses, speeds, attacks and reach. Never substitute the
 * host body when a selected form or its template overlay cannot be compiled. */
export function resolveCreatureShapeshiftForm(host: ItemFieldRow, deps: CreatureAttacksDependencies): CreatureShapeshiftFormResult {
    const readers = createFormulaReadContext(deps);
    let form: ItemFieldRow | null = null;
    for (const status of Object.values(readCreaturePayloadStatuses(host, readers.layerValue, deps.resolveCarrierRef))) {
        if (!arrayLike(status)) continue;
        const def = status.status_arr;
        if (!arrayLike(def)) return { ok: false, reason: 'status-undefined' };
        const type = readers.itemField(status, 'item_type');
        if (type == null) return { ok: false, reason: 'status-type-undefined' };
        if (type !== 'creature' || form) continue;
        if (readItemUuidSlot(readers.itemField(status, 'status_shapeshift_monster_template_id'))) {
            return { ok: false, reason: 'shapeshift-template' };
        }
        let resolved: ItemFieldRow | null = def;
        if (!arrayLike(def.item_layer_types)) {
            const id = readItemUuidSlot(readers.itemField(def, 'item_id'))
                || readItemUuidSlot(readers.itemField(status, 'item_id'))
                || readItemUuidSlot(readers.itemField(status, 'status_item_id'));
            resolved = id ? deps.queryFullItem(id) : null;
        }
        if (!arrayLike(resolved) || !arrayLike(resolved.item_layer_types)) {
            return { ok: false, reason: 'shapeshift-form-undefined' };
        }
        form = resolved;
    }
    return { ok: true, form };
}
